import { lazy } from 'react';
import convertRouteGroupsToRoutes from '@/lib/convertRouteGroupsToRoutes.js';
import GoToMindCloudScreen from './screens/auth/GoToMindCloudScreen';

const OauthScreen = lazy(() => import('./screens/oauth/OauthScreen'));
const CredentialScreen = lazy(() => import('./screens/credentials/CredentialScreen'));
const WorkflowScreen = lazy(() => import('./screens/workflows/WorkflowScreen'));
const AdminSettingsScreen = lazy(() => import('@/screens/adminSettings/AdminSettingsScreen.jsx'));
const JobsScreen = lazy(() => import('./screens/jobs/JobsScreen'));
const LoginV2Screen = lazy(() => import('./screens/auth/LoginV2Screen'));
const DocsAuthBridgeScreen = lazy(() => import('./screens/auth/DocsAuthBridgeScreen'));
const DesktopAuthCompleteScreen = lazy(() => import('./screens/auth/DesktopAuthCompleteScreen'));
const SignUpScreen = lazy(() => import('./screens/auth/SignUpScreen'));
const SignUpScreenAlt = lazy(() => import('./screens/auth/SignUpScreenAlt'));
const SsoCallback = lazy(() => import('./screens/auth/SsoCallback'));
const DesignScreen = lazy(() => import('./screens/design/DesignScreen'));
const AppsScreen = lazy(() => import('@/screens/apps/AppsScreen'));
const UserScreen = lazy(() => import('@/screens/user/UserScreen'));
const MonitoringScreen = lazy(() => import('@/screens/monitoring/MonitoringScreen'));
const HomeScreen = lazy(() => import('@/screens/home/HomeScreen.jsx'));
const HomeWhiteGlove = lazy(() => import('@/screens/home/HomeWhiteGlove.jsx'));
const RecipesScreen = lazy(() => import('./screens/recipes/RecipesScreen'));
const TutorialAuthScreen = lazy(() => import('./screens/tutorialAuth/TutorialAuthScreen'));
const TutorialWebhookScreen = lazy(() => import('./screens/tutorialWebhook/TutorialWebhookScreen'));
const EmbeddedScreen = lazy(() => import('./screens/embedded/EmbeddedScreen'));
const EmbeddedInstallScreen = lazy(() => import('./screens/embeddedInstall/EmbeddedInstallScreen'));
const EmbeddedExampleScreen = lazy(() => import('./screens/embeddedExample/EmbeddedExampleScreen'));
const TurnkeyIntegrationsScreen = lazy(() => import('./screens/turnkeyIntegrations/TurnkeyIntegrationsScreen'));
const WorkflowBlueprintPublicScreen = lazy(() => import('./screens/workflowBlueprintPublic/WorkflowBlueprintPublicScreen'));

// Route ids are positional (convertRouteGroupsToRoutes): append new groups at
// the end — inserting mid-object renumbers activeRouteId for every later route.
const routeGroups = {
  workflows: {
    element: WorkflowScreen,
    permissions: ['workflow:view'],
    paths: [
      '/workflows', //
      '/workflows/:workflowId', // necessary so we render WorkflowStateHandler.jsx to support backward compatibility URLs
      '/workflows/:workflowId/:workflowVersion',
      '/workflows/:workflowId/:workflowVersion/:actionId',
      '/workflows/:workflowId/:workflowVersion/:actionId/:stepId',
      '/workflows/:workflowId/:workflowVersion/:actionId/:stepId/:tab',
      '/workflows/:workflowId/:workflowVersion/:actionId/:stepId/:tab/:subTab',
      '/workflows/:workflowId/:workflowVersion/:actionId/:stepId/:tab/:subTab/:extra'
    ]
  },

  credentials: {
    element: CredentialScreen,
    permissions: ['credential:view'],
    paths: ['/credentials', '/credentials/:credentialId']
  },

  recipes: {
    element: RecipesScreen,
    permissions: ['workflow:view'],
    paths: ['/recipes', '/recipes/:tab', '/recipes/:tab/:subTab']
  },

  home: {
    element: HomeScreen,
    permissions: [],
    paths: ['/', '/home']
  },

  apps: {
    element: AppsScreen,
    permissions: ['app:view'],
    paths: [
      '/apps', //
      '/apps/:appId',
      '/apps/:appId/:version',
      '/apps/:appId/:version/:section',
      '/apps/:appId/:version/:section/:arg1',
      '/apps/:appId/:version/:section/:arg1/:arg2',
      '/apps/:appId/:version/:section/:arg1/:arg2/:arg3',
      '/apps/:appId/:version/:section/:arg1/:arg2/:arg3/:arg4',
      '/apps/:appId/:version/:section/:arg1/:arg2/:arg3/:arg4/:arg5'
    ]
  },

  embedded: {
    element: EmbeddedScreen,
    permissions: ['embedded:view'],
    paths: [
      '/embedded', //
      '/embedded/:integrationId'
    ]
  },

  embeddedExample: {
    element: EmbeddedExampleScreen,
    paths: ['/embedded-example'],
    appHeader: false
  },

  turnkeyIntegrations: {
    element: TurnkeyIntegrationsScreen,
    paths: ['/turnkey-integrations']
  },

  jobs: {
    element: JobsScreen,
    permissions: ['job:view'],
    paths: [
      '/jobs', //
      '/jobs/:jobId',
      '/jobs/:jobId/:jobSubpage',
      '/jobs/:jobId/:jobSubpage/:subpageId',
      '/jobs/:jobId/:jobSubpage/:subpageId/:section'
    ],
    fixedWidth: true
  },

  adminSettings: {
    element: AdminSettingsScreen,
    permissions: ['developer:all'],
    paths: [
      '/admin-settings/:category/:categoryId', //
      '/admin-settings/:category',
      '/admin-settings'
    ]
  },

  monitoring: {
    element: MonitoringScreen,
    permissions: ['developer:all'],
    paths: ['/monitoring']
  },

  design: {
    element: DesignScreen,
    paths: ['/design']
  },

  oauth: {
    element: OauthScreen,
    paths: ['/oauth/:appSlug', '/oauth/:appSlug/:response'],
    appHeader: false
  },

  tutorialAuth: {
    element: TutorialAuthScreen,
    paths: ['/tutorial-auth'],
    appHeader: false,
    unauthenticated: true
  },

  tutorialWebhook: {
    element: TutorialWebhookScreen,
    paths: ['/tutorial-webhook'],
    appHeader: false,
    unauthenticated: true
  },

  signup: {
    element: SignUpScreen,
    paths: ['/signup/private/r02f5o2otsrqpkist7djyhbjbvw9oulby5fejod2lcdkul7kjhlpbve9hwxdnluc'],
    appHeader: false,
    unauthenticated: true
  },

  privateBetaSignUp: {
    element: SignUpScreen,
    paths: ['/signup-old/*'],
    appHeader: false,
    unauthenticated: true
  },

  signupAlt: {
    element: SignUpScreenAlt,
    paths: ['/signup/*'],
    appHeader: false,
    unauthenticated: true
  },

  ssoCallback: {
    element: SsoCallback,
    paths: ['/sso-callback'],
    appHeader: false,
    unauthenticated: true
  },

  desktopAuthComplete: {
    element: DesktopAuthCompleteScreen,
    paths: ['/desktop-auth/complete'],
    appHeader: false,
    unauthenticated: true
  },

  login: {
    element: LoginV2Screen,
    paths: ['/login/*'],
    appHeader: false,
    unauthenticated: true
  },

  docsAuth: {
    element: DocsAuthBridgeScreen,
    paths: ['/docs-auth'],
    appHeader: false,
    unauthenticated: true
  },

  userSettings: {
    element: UserScreen,
    paths: ['/user/:section']
  },

  goToMindCloud: {
    element: GoToMindCloudScreen,
    paths: ['/go-to-mindcloud'],
    appHeader: false,
    unauthenticated: true
  },

  workflowBlueprintPublic: {
    element: WorkflowBlueprintPublicScreen,
    paths: ['/workflow-blueprint/:token'],
    appHeader: false,
    unauthenticated: true
  },

  embeddedInstall: {
    element: EmbeddedInstallScreen,
    paths: ['/embedded-install'],
    appHeader: false,
    unauthenticated: true
  },

  fullService: {
    element: HomeWhiteGlove,
    permissions: [],
    paths: ['/full-service']
  }
};

const routes = convertRouteGroupsToRoutes(routeGroups);

export const unauthenticatedRoutes = routes.filter((n) => n.unauthenticated === true);

export default routes;
